import { useState, useEffect, useRef, useMemo, type ChangeEvent } from "react";
import { format } from "date-fns";
import axios from "../../plugins/axios";
import { useAppSelector, useAppDispatch } from "../../store/hooks";
import { fetchHiscoxMembers } from "../../store/slices/registrationSlice";
import {
    Box,
    Button,
    TextField,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableFooter,
    TablePagination,
} from "@mui/material";

const apiUrl = process.env.REACT_APP_WORKNEST_TOOLS_API_URL;

const Hiscox = () => {
    const [submitting, setSubmitting] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { hiscoxMembers: members, fetchingHiscoxMembers: fetching } = useAppSelector(
        (state) => state.registrationReducer,
    );
    const dispatch = useAppDispatch();

    const [filter, setFilter] = useState("");
    const [page, setPage] = useState(0);

    const rowsPerPage = 10;

    useEffect(() => {
        if (members.length === 0) dispatch(fetchHiscoxMembers());
    }, [dispatch, members.length]);

    const activeFilter = useMemo(() => {
        if (filter.length < 3) return "";
        setPage(0);
        return filter.toLowerCase();
    }, [filter]);

    const filteredMembers = useMemo(() => {
        const base = members ?? [];
        return activeFilter === ""
            ? base
            : base.filter((f) => f.name.toLowerCase().includes(activeFilter));
    }, [activeFilter, members]);

    const displayedMembers = useMemo(
        () => filteredMembers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [filteredMembers, page],
    );

    const handleUpload = async (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("file", file);

        setSubmitting(true);
        try {
            const result = await axios.post(`${apiUrl}/registration/upload`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            console.log(result.data);
            setPage(0);
            dispatch(fetchHiscoxMembers());
        } catch (err) {
            console.error(err);
        } finally {
            setSubmitting(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    return (
        <>
            <Box display="flex" style={{ marginTop: "10px" }} alignItems="center" gap="10px" flexWrap="wrap">
                <TextField
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    variant="outlined"
                    size="small"
                    label="filter"
                />
                <Button
                    onClick={() => fileInputRef.current?.click()}
                    variant="contained"
                    disabled={submitting}
                    color="primary"
                >
                    Upload
                </Button>
                <input
                    hidden
                    ref={fileInputRef}
                    accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    type="file"
                    onChange={handleUpload}
                />
            </Box>
            {fetching && <h2 style={{ margin: "10px" }}>Loading ...</h2>}
            {!fetching && members.length === 0 && <h2 style={{ margin: "10px" }}>No entries</h2>}
            {!fetching && members.length > 0 && (
                <Table size="small">
                    <TableHead>
                        <TableRow style={{ height: "37px" }}>
                            <TableCell>Name</TableCell>
                            <TableCell>Policy Number</TableCell>
                            <TableCell>Post Code</TableCell>
                            <TableCell>Start</TableCell>
                            <TableCell>End</TableCell>
                            <TableCell>Endorsement</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Channel</TableCell>
                            <TableCell>Broker</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {displayedMembers.map((m) => (
                            <TableRow key={m.policyNumber}>
                                <TableCell>{m.name}</TableCell>
                                <TableCell>{m.policyNumber}</TableCell>
                                <TableCell>{m.postCode}</TableCell>
                                <TableCell>{format(new Date(m.startDate), "dd/MM/yyyy")}</TableCell>
                                <TableCell>{format(new Date(m.endDate), "dd/MM/yyyy")}</TableCell>
                                <TableCell>{format(new Date(m.endorsementDate), "dd/MM/yyyy")}</TableCell>
                                <TableCell>{m.status}</TableCell>
                                <TableCell>{m.channel}</TableCell>
                                <TableCell>
                                    {m.brokerCompanyName}
                                    {m.brokerNumber && <> ({m.brokerNumber})</>}
                                    {m.brokerContact && (
                                        <span style={{ fontSize: "0.8em" }}>
                                            <br />
                                            {m.brokerContact}
                                        </span>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                rowsPerPageOptions={[]}
                                count={filteredMembers.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={(_e, newPage) => setPage(newPage)}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            )}
        </>
    );
};

export default Hiscox;
